import gateway from '../gateway/gateway'
import { Action as ActionType, Dispatch } from '../types/reducer'
import { Tag } from '../types/tag'
import { captureException } from '../sentry-config'

const toTag = (tagName: string): Tag => ({ key: tagName, name: tagName, state: 'old' })

export const ASSIGN_TEAM_TAGS = 'tags/ASSIGN_TEAM_TAGS'
export const ASSIGN_TAGS = 'tags/ASSIGN_TAGS'
export const APPEND_TAGS = 'tags/APPEND_TAGS'
export const DELETE_TAG = 'tags/DELETE_TAG'
export const APPEND_TEAM_TAGS = 'tags/APPEND_TEAM_TAGS'

type AssignTeamTagsAction = ActionType<typeof ASSIGN_TEAM_TAGS, Tag[]>
type AssignTagsAction = ActionType<typeof ASSIGN_TAGS, Tag[]>
type AppendTagsAction = ActionType<typeof APPEND_TAGS, Tag[]>
type DeleteTagAction = ActionType<typeof DELETE_TAG, Tag>
type AppendTeamTagsAction = ActionType<typeof APPEND_TEAM_TAGS, Tag[]>

type Action =
  | AssignTeamTagsAction
  | AssignTagsAction
  | AppendTagsAction
  | DeleteTagAction
  | AppendTeamTagsAction

export type State = {
  teamTags: Tag[]
  tags: Tag[]
}

const initialState: State = {
  teamTags: [],
  tags: [],
}

export default function tagsReducer(state: State = initialState, action: Action): State {
  switch (action.type) {
    case ASSIGN_TEAM_TAGS:
      return {
        ...state,
        teamTags: action.payload,
      }
    case ASSIGN_TAGS:
      return {
        ...state,
        tags: action.payload,
      }
    case APPEND_TAGS:
      return {
        ...state,
        tags: [...state.tags, ...action.payload],
      }
    case APPEND_TEAM_TAGS:
      return {
        ...state,
        teamTags: [...state.teamTags, ...action.payload],
      }
    case DELETE_TAG:
      return {
        ...state,
        tags: state.tags.filter(tag => tag.name !== action.payload.name),
      }
    default:
      return state
  }
}

export const assignTeamTags = (tagNames: string[]): AssignTeamTagsAction => ({
  type: ASSIGN_TEAM_TAGS,
  payload: tagNames.map(toTag),
})

export const assignTags = (tagNames: string[]): AssignTagsAction => ({
  type: ASSIGN_TAGS,
  payload: tagNames.map(toTag),
})

export const appendTags = (tagNames: string[]): AppendTagsAction => ({
  type: APPEND_TAGS,
  payload: tagNames.map(toTag),
})

export const appendTeamTags = (tagNames: string[]): AppendTeamTagsAction => ({
  type: APPEND_TEAM_TAGS,
  payload: tagNames.map(toTag),
})

export const deleteTag = (tag: Tag): DeleteTagAction => ({
  type: DELETE_TAG,
  payload: tag,
})

export const getTeamTags = () => (dispatch: Dispatch<AssignTeamTagsAction, void>) =>
  gateway.tags
    .team()
    .then((tagNames: string[]) => dispatch(assignTeamTags(tagNames)))
    .catch(captureException)

export const getTags = (userId: number) => (dispatch: Dispatch<AssignTagsAction, void>) =>
  gateway.tags
    .allForUserId(userId)
    .then((tagNames: string[]) => dispatch(assignTags(tagNames)))
    .catch(captureException)

export const addTags = (userId: number, tagNames: string[]) => (
  dispatch: Dispatch<AppendTagsAction, void>
) =>
  gateway.tags
    .createForUserId(userId, tagNames)
    .then(() => dispatch(appendTags(tagNames)))
    .catch(captureException)

export const addTeamTags = (tagNames: string[]) => (
  dispatch: Dispatch<AppendTeamTagsAction, void>
) => {
  dispatch(appendTeamTags(tagNames))
}

export const removeTag = (userId: number, tag: Tag) => (
  dispatch: Dispatch<DeleteTagAction, void>
) =>
  gateway.tags
    .removeForUserId(userId, tag.name)
    .then(() => dispatch(deleteTag(tag)))
    .catch(captureException)
